/**
 * Ask the Brain and Ask Zou4Mo, as a chat overlay rather than two panels on the board.
 *
 * WHY IT LEFT THE COLUMNS. He reported it directly: "the Chat, and Ask Zou they need to be a pop
 * chat They are wrong inside the dashboard". He was right, and the measurement agreed. A question
 * takes about 25 seconds and a drafted proposal is several hundred words, so both need reading
 * room and a place to keep a thread. Inside a dashboard column they had neither: they competed
 * for height with panels that answer a different question, and the deck's Context column was
 * already carrying 2.5x its own height in content. He was zooming the browser to 50% to cope.
 *
 * A dashboard answers "what is outstanding" at a glance. A conversation is the opposite shape:
 * it wants focus, it wants history, and it is not glanceable. Putting the two in one scroll
 * column made each one worse.
 *
 * WHAT MAKES IT A CHAT RATHER THAN A FORM, which was his other complaint ("Zou is not cleary
 * helping (No Send button)"):
 *
 *  - A visible **Send** button, and Enter to send with Shift+Enter for a newline. The old Zou4Mo
 *    surface had one button labelled "Draft" and four form fields, which reads as a report
 *    generator, not something you talk to.
 *  - A **thread**, so the previous turn is on screen. Both surfaces already carried history to
 *    the server; neither showed it, so a follow-up looked like it had been ignored.
 *  - His message stays in the thread while the answer is being worked on, with the honest
 *    waiting line rather than a spinner.
 *
 * WHAT IT KEEPS. Both lanes still answer with their own contract, and the overlay does not
 * flatten them: an Ask the Brain turn carries verified citations, a Zou4Mo turn carries caveats
 * and a Keep-in-vault action. That distinction is the whole reason there are two tabs and not one
 * box, and the header says which lane is which so it is never ambiguous which he is reading.
 */

import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Aurebesh } from './Aurebesh.js';
import { Companion } from './Companion.js';
import { FalconBank, FalconSwitches } from './FalconPanel.js';
import { Icon } from './Icon.js';
import {
  askBrain,
  downloadPptx,
  draftMaterial,
  openDeck,
  saveMaterial,
  type BrainAnswer,
  type Brand,
  type Material,
  type MaterialKind,
} from './api.js';

type Lane = 'brain' | 'zou';

/**
 * Open the ask overlay from anywhere, pre-filled.
 *
 * A DOM event rather than shared state, because the launcher owns whether the overlay is mounted
 * and the callers (an agenda row today, an account row tomorrow) have no relationship to it. The
 * alternative was lifting overlay state into the app store, which would make every surface that
 * wants to offer a prompt a dependency of the overlay.
 *
 * It exists because he should not have to remember Zou4Mo is there, work out what his meeting is
 * called, and type it. That is the step "Prepare" removes.
 */
export interface AskRequest {
  lane: Lane;
  kind?: MaterialKind;
  about: string;
  /** Send immediately, or leave it in the box for him to edit first. Default is to send. */
  hold?: boolean;
}

const ASK_EVENT = 'mo:ask';

export function openAsk(req: AskRequest): void {
  globalThis.dispatchEvent(new CustomEvent<AskRequest>(ASK_EVENT, { detail: req }));
}

/**
 * The thread lives OUTSIDE the component, for the session.
 *
 * It used to be `useState` inside the overlay, so closing it threw the conversation away. That is
 * expensive in a way a chat usually is not: a brain answer costs about 25 seconds and a Zou draft
 * costs a model call and several hundred words. Pressing Escape by reflex, or clicking the
 * backdrop, silently spent both again. Now closing the overlay puts it away rather than burning
 * it, and reopening resumes.
 *
 * Deliberately in memory and not `sessionStorage`: an answer carries citations to client notes,
 * and a reload is a reasonable place for that to be gone.
 */
let threadStore: Turn[] = [];
/** Cleared on demand, and reported so the tests can prove close-then-open keeps the thread. */
export function clearChatThread(): void {
  threadStore = [];
}

/** One exchange. `pending` is the turn currently being worked on. */
interface Turn {
  lane: Lane;
  question: string;
  pending: boolean;
  answer?: BrainAnswer;
  material?: Material;
  error?: string;
  /** Stopped by him rather than failed, so it offers "ask again" and not "something broke". */
  aborted?: boolean;
  /** What Zou was asked for, kept per turn so a retry cannot silently change the kind. */
  kind?: MaterialKind;
  brand?: Brand;
  /** Per-turn, because one `savedPath` for the whole overlay marked every draft as saved. */
  savedPath?: string;
  saveError?: string;
  pptxError?: string;
}

/**
 * Which livery, inferred from the subject.
 *
 * The server's own contract says the brand is "asked or inferred from the account, never defaulted
 * silently, because a vantage proposal in ntam livery is the kind of error he would have to notice
 * himself in front of a client". The client then hardcoded `'ntam'`, which is exactly that silent
 * default: every deck came out in group livery unless he happened to spot a dropdown sitting
 * between two others.
 *
 * The rule is deliberately NARROW. Nothing in the vault records which account is vantage work:
 * orgs carry no `brand` field and the vantage mount lists one client, so there is no list to match
 * against. Rather than guess from the account name, this fires only on an explicit mention and
 * otherwise leaves the choice visible and unmade. A wrong guess here is worse than no guess,
 * because the brand changes the PROSE as well as the plates and he cannot fix it after drafting.
 */
export function inferBrand(about: string): { brand: Brand; why: string } | null {
  if (/\bvantage\b/i.test(about)) {
    return { brand: 'vantage', why: 'the subject names vantage' };
  }
  return null;
}

const KINDS: { value: MaterialKind; label: string }[] = [
  { value: 'proposal-outline', label: 'Proposal outline' },
  { value: 'follow-up-email', label: 'Follow-up email' },
  { value: 'pre-meeting-briefing', label: 'Pre-meeting briefing' },
  /* Distinct from the briefing above, which is built on the record. This one is for a
     counterparty the vault barely knows, so it leans on the method instead. See zou.ts. */
  { value: 'first-meeting-plan', label: 'First meeting plan' },
  { value: 'talking-points', label: 'Talking points' },
  { value: 'deck-outline', label: 'Deck' },
];

export function ChatOverlay({
  onClose,
  initial,
}: {
  onClose: () => void;
  /** Pre-filled request from another surface, sent once on mount. See `openAsk`. */
  initial?: AskRequest | null;
}): JSX.Element {
  const [lane, setLane] = useState<Lane>(initial?.lane ?? 'brain');
  const [text, setText] = useState(initial?.about ?? '');
  /* Seeded from the session store, so closing the overlay puts the thread away rather than
     throwing away answers that cost 25 seconds each. */
  const [turns, setTurns] = useState<Turn[]>(() => threadStore);
  const [busy, setBusy] = useState(false);

  /* Mirror every change back to the store. One effect rather than writing at each call site: a
     `setTurns` that forgets to mirror loses exactly one answer, which is the hardest kind of bug
     to notice here. */
  useEffect(() => {
    threadStore = turns;
  }, [turns]);

  /*
   * The in-flight request, so it can be stopped.
   *
   * Aborted on unmount as well as on the Stop button: closing the overlay used to leave the
   * request running and land a `setState` on a component that no longer exists.
   */
  const flight = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      flight.current?.abort();
    },
    [],
  );

  // Zou4Mo still needs a kind and a brand. They live in the composer bar rather than a form:
  // brand is never defaulted silently, because a vantage proposal in ntam livery is an error he
  // would have to catch himself in front of a client.
  const [kind, setKind] = useState<MaterialKind>(initial?.kind ?? 'follow-up-email');
  /*
   * The livery follows the subject until he says otherwise.
   *
   * `brandPinned` is what makes that safe: once he picks a livery himself, inference stops
   * touching it. Without the pin, typing "vantage" into a subject after choosing ntam would
   * silently move it back, which is the same class of error as the silent default it replaces.
   */
  const [brand, setBrand] = useState<Brand>(() => inferBrand(initial?.about ?? '')?.brand ?? 'ntam');
  const [brandPinned, setBrandPinned] = useState(false);
  const inferred = inferBrand(text);
  useEffect(() => {
    if (brandPinned || inferred === null) return;
    setBrand(inferred.brand);
  }, [brandPinned, inferred?.brand]);

  const threadRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [lane]);

  /*
   * The field grows with the message, to a ceiling.
   *
   * It was fixed at two rows, so a 300-word brief was written and re-read through a two-line
   * window. Capped at 40% of the panel, or the thread it belongs to disappears behind it.
   */
  useEffect(() => {
    const el = inputRef.current;
    if (el === null) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [text, lane]);

  // Escape closes, which is what every overlay on the web does and what his hands will try.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') escape();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
    // `escape` closes over `busy`, which is the point: it must see the current value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, busy]);

  // Follow the thread down as answers land, so a long reply does not leave him at the top of it.
  useEffect(() => {
    const el = threadRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [turns]);

  /**
   * Ask, in the current lane.
   *
   * `override` is how a retry re-asks a turn exactly as it was first asked: the lane, kind and
   * brand come from the turn rather than from whatever the composer happens to be set to now.
   * Retrying a vantage proposal in ntam livery because he had since flipped the brand selector is
   * the kind of error he would only find in front of a client.
   */
  const send = async (override?: { question: string; lane: Lane; kind?: MaterialKind; brand?: Brand }): Promise<void> => {
    const q = (override?.question ?? text).trim();
    if (q === '' || busy) return;
    const askLane = override?.lane ?? lane;
    const askKind = override?.kind ?? kind;
    const askBrand = override?.brand ?? brand;
    if (override === undefined) setText('');
    setBusy(true);

    const controller = new AbortController();
    flight.current = controller;

    let index = -1;
    setTurns((t) => {
      index = t.length;
      return [...t, { lane: askLane, question: q, pending: true, kind: askKind, brand: askBrand }];
    });

    /*
     * History is built from the turns of THIS LANE only. Feeding a drafted proposal back into
     * Ask the Brain as prior context would teach it to treat composed text as something the
     * record said, which is the one confusion this whole surface is built to prevent.
     */
    const history = turns
      .filter((t) => t.lane === askLane && !t.pending)
      .map((t) => ({
        question: t.question,
        answer: t.answer?.answer ?? (t.material?.sections ?? []).map((s) => `${s.heading}: ${s.body}`).join('\n'),
      }))
      .filter((h) => h.answer !== '')
      .slice(-8);

    const finish = (patch: Partial<Turn>): void =>
      setTurns((t) => t.map((x, i) => (i === index ? { ...x, pending: false, ...patch } : x)));

    if (askLane === 'brain') {
      const res = await askBrain(q, history, controller.signal);
      finish(
        res.ok
          ? { answer: res.value }
          : { error: res.error, aborted: res.aborted === true },
      );
    } else {
      const res = await draftMaterial(
        { kind: askKind, about: q, brand: askBrand, history },
        controller.signal,
      );
      finish(
        res.state === 'done'
          ? { material: res.material }
          : { error: res.message, aborted: res.aborted === true },
      );
    }
    flight.current = null;
    setBusy(false);
    inputRef.current?.focus();
  };

  /** Stop the current request. Reported as stopped, not failed. */
  const stop = (): void => {
    flight.current?.abort();
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    void send();
  };

  /*
   * Escape closes, EXCEPT while something is in flight.
   *
   * Reflex-closing a 25-second answer was one of the ways this surface quietly wasted his time.
   * With a request running, Escape stops it instead, and a second press closes.
   */
  const escape = (): void => {
    if (busy) {
      stop();
      return;
    }
    onClose();
  };

  /*
   * Send a pre-filled request when one arrives, EVERY time a new one arrives.
   *
   * It used to guard with a plain `sentInitial` boolean, which fixed one bug and caused a worse
   * one: the guard is per-mount, so pressing Prepare on a second meeting while the overlay was
   * already open did NOTHING AT ALL. No new turn, no error, no clue. The offer that exists so he
   * does not have to remember Zou4Mo silently stopped working exactly when he was using it.
   *
   * The guard is now per-REQUEST, keyed on the request object identity. A re-render cannot fire
   * the same one twice, which was the original concern (two model calls, the same briefing in
   * duplicate), and a genuinely new request is answered.
   */
  const lastSent = useRef<AskRequest | null>(null);
  useEffect(() => {
    if (initial === null || initial === undefined) return;
    if (lastSent.current === initial) return;
    lastSent.current = initial;
    setLane(initial.lane);
    if (initial.kind !== undefined) setKind(initial.kind);
    if (initial.hold === true) {
      setText(initial.about);
      inputRef.current?.focus();
      return;
    }
    setText('');
    void send({ question: initial.about, lane: initial.lane, kind: initial.kind ?? kind, brand });
    // `send` is stable enough for this: it reads the refs and the state it needs at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  /*
   * Keep and Download report on THEIR OWN turn.
   *
   * Both used one piece of overlay-wide state, so saving the third draft printed "Saved to ..."
   * under the first one as well: the message appeared on every material in the thread at once.
   * A per-turn patch is the only version that can be read correctly with more than one draft on
   * screen, which is the normal case after two questions.
   */
  const patch = (index: number, p: Partial<Turn>): void =>
    setTurns((t) => t.map((x, i) => (i === index ? { ...x, ...p } : x)));

  const onKeep = async (index: number, m: Material): Promise<void> => {
    patch(index, { savedPath: undefined, saveError: undefined });
    const res = await saveMaterial(m);
    patch(index, res.ok ? { savedPath: res.path } : { saveError: res.error });
  };

  const onPptx = async (index: number, m: Material): Promise<void> => {
    patch(index, { pptxError: undefined });
    const res = await downloadPptx(m);
    if (!res.ok) patch(index, { pptxError: res.error });
  };

  return createPortal(
    <div className="mo-chat-scrim" role="dialog" aria-modal="true" aria-label="Ask">
      {/* Clicking the backdrop closes. The panel stops the click so a stray click inside does not. */}
      <button type="button" className="mo-chat-backdrop" aria-label="Close" onClick={onClose} />

      <div className="mo-chat">
        <header className="mo-chat-head">
          <img className="mo-chat-emblem" src="/deck-emblem.webp" alt="" width={30} height={30} />
          {/* Frame furniture, the same strip the deck's rail and hero carry. Decoration by his
              instruction: it spells nothing. See mo/Aurebesh.tsx. */}

          <div className="mo-tabstrip" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={lane === 'brain'}
              className="mo-tab"
              data-active={lane === 'brain'}
              onClick={() => setLane('brain')}
            >
              Ask the Brain
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={lane === 'zou'}
              className="mo-tab"
              data-active={lane === 'zou'}
              onClick={() => setLane('zou')}
            >
              Ask Zou4Mo
            </button>
          </div>
          {/*
            Which lane he is in, said as a claim about TRUST rather than a feature list. This is
            the line that stops a composed proposal being read as something the record says, and
            it is the reason there are two lanes and not one box.
          */}
          <span className="mo-chat-what" data-lane={lane}>
            <span className="mo-chat-what-dot" aria-hidden="true" />
            {lane === 'brain'
              ? 'Answers from the record, and cites the notes it used.'
              : 'Composes new text. Nothing it writes is a record of anything.'}
          </span>
          {turns.length > 0 && (
            <button
              type="button"
              className="mo-chat-clear"
              onClick={() => {
                clearChatThread();
                setTurns([]);
              }}
              title="Clear this conversation"
            >
              Clear
            </button>
          )}
          <button type="button" className="mo-chat-close" onClick={escape} aria-label="Close">
            ×
          </button>
        </header>

        {/*
          THE OVERHEAD PANEL. Its own strip rather than crowded into the header, which is both
          where a cockpit actually puts a light bank and the fix for the header wrapping its tabs
          onto two lines once the bank was in it. The Aurebesh moved out with it: the bank is the
          decorative anchor now and two of them side by side was one too many.

          It is a READOUT, not blinking lights: one cell per exchange, in that exchange's lane
          colour, the one in flight pulsing. See mo/FalconPanel.tsx.
        */}
        <div className="mo-chat-overhead">
          <FalconBank turns={turns.map((t) => ({ lane: t.lane, pending: t.pending }))} />
          {/* Frame, and stated as frame: these carry nothing. Beside the bank rather than down
              the composer's gutter, where they collided with the conduit and crowded the
              controls; a real panel groups its lights and its switches together anyway. */}
          <FalconSwitches count={9} />
          <Aurebesh className="mo-chat-stencil" seed="chat-console" length={6} size={10} />
        </div>

        {/*
          THE COMPANION, present the WHOLE time rather than only on the empty state. That is what
          he meant by "more integrating in the Design": a figure that vanishes the moment you talk
          to it was decoration for an empty box, not a companion. It replaces the old `char-ask`
          plate, which only ever appeared before the first question.

          It reacts for exactly two reasons and both are true statements about the machine: it
          looks toward the pointer, and it leans in and pulses in the lane's colour while a model
          call is in flight. See mo/Companion.tsx on why it does nothing else.
        */}
        <Companion lane={lane} waiting={turns.some((t) => t.pending)} />

        <div className="mo-chat-thread" ref={threadRef}>
          {turns.length === 0 && (
            <div className="mo-chat-empty">
              <p>
                {lane === 'brain'
                  ? 'Ask anything the vault might know. It breaks the question into several searches, reads what it finds, and answers with links to the notes it used.'
                  : 'Name an account, engagement or person and it drafts the material. Everything it writes is composed rather than observed, so it comes back with its caveats attached.'}
              </p>
              {/*
                Starters, not decoration. The blank box was the hardest part of this surface to
                use: it answers "what can I even ask" without him having to guess, and every one
                of these is a question the lane can actually answer well.
              */}
              <p className="mo-chat-starters-label">Try</p>
              <div className="mo-chat-starters">
                {(lane === 'brain' ? BRAIN_STARTERS : ZOU_STARTERS).map((q) => (
                  <button
                    key={q}
                    type="button"
                    className="mo-chat-starter"
                    onClick={() => {
                      setText(q);
                      inputRef.current?.focus();
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((t, i) => (
            <div key={`${i}-${t.question}`} className="mo-chat-turn" data-lane={t.lane}>
              <p className="mo-chat-mine">{t.question}</p>

              {t.pending && (
                <div className="mo-chat-waiting">
                  <span className="mo-chat-pulse" aria-hidden="true" />
                  <span>
                    {t.lane === 'brain'
                      ? 'Searching from several angles, then reading what it finds.'
                      : 'Reading the record, then drafting.'}{' '}
                    <Elapsed />
                  </span>
                  {/* A stop, because 25 seconds is long enough to change his mind and closing the
                      overlay used to be the only way out. */}
                  <button type="button" className="mo-chat-stop" onClick={stop}>
                    Stop
                  </button>
                </div>
              )}

              {t.error !== undefined && (
                <div className="mo-chat-error" data-stopped={t.aborted === true}>
                  <p>{t.aborted === true ? 'Stopped before it answered.' : t.error}</p>
                  {/* Ask again re-sends the turn AS IT WAS ASKED, not as the composer is set now. */}
                  <button
                    type="button"
                    className="mo-chat-retry"
                    disabled={busy}
                    onClick={() =>
                      void send({
                        question: t.question,
                        lane: t.lane,
                        kind: t.kind,
                        brand: t.brand,
                      })
                    }
                  >
                    Ask again
                  </button>
                </div>
              )}

              {t.answer !== undefined && (
                <div className="mo-chat-reply">
                  <p className="mo-chat-body">{t.answer.answer}</p>
                  {/* Caveats are never folded: they change how much of the answer to believe. */}
                  {t.answer.caveats.length > 0 && (
                    <ul className="mo-chat-caveats">
                      {t.answer.caveats.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  )}
                  {t.answer.citations.length > 0 && (
                    <p className="mo-chat-cites">
                      From: {t.answer.citations.join(', ')}
                    </p>
                  )}
                </div>
              )}

              {t.material !== undefined && (
                <div className="mo-chat-reply">
                  <p className="mo-chat-livery" data-brand={t.material.brand}>
                    <span aria-hidden="true" />
                    {t.material.brand === 'vantage' ? 'vantage livery' : 'ntam group livery'}
                  </p>
                  <h4 className="mo-chat-title">{t.material.title}</h4>
                  {t.material.caveats.length > 0 && (
                    <ul className="mo-chat-caveats">
                      {t.material.caveats.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  )}
                  {t.material.sections.map((s) => (
                    <div key={s.heading + s.body.slice(0, 24)} className="mo-chat-section">
                      <h5>{s.heading}</h5>
                      <p>{s.body}</p>
                    </div>
                  ))}
                  {t.material.citations.length > 0 && (
                    <p className="mo-chat-cites">Drawn from: {t.material.citations.join(', ')}</p>
                  )}
                  <div className="mo-chat-actions">
                    <button type="button" onClick={() => void onKeep(i, t.material!)}>
                      <Icon name="notes" size={13} />
                      Keep in vault
                    </button>
                    {/* Any material can be shown as a deck, not just `deck-outline`: he sometimes
                        wants a proposal outline on screen in the brand for a call. */}
                    <button type="button" onClick={() => void openDeck(t.material!)}>
                      <Icon name="graph" size={13} />
                      Open as deck
                    </button>
                    {/* A real PowerPoint, rendered by the brand's own engine in the sidecar. It
                        downloads rather than opening, and it reports why on failure instead of
                        quietly handing him the HTML version. */}
                    {/* The livery is named ON the button, because the .pptx is the one artefact
                        that leaves MO OS and gets presented where nothing else says which brand
                        it is in. */}
                    <button type="button" onClick={() => void onPptx(i, t.material!)}>
                      <Icon name="action" size={13} />
                      Download .pptx
                      <span className="mo-chat-inbrand">
                        {t.material.brand === 'vantage' ? 'vantage' : 'ntam'}
                      </span>
                    </button>
                    {t.savedPath !== undefined && (
                      <span className="mo-chat-note" data-ok="true">
                        Saved to {t.savedPath}, marked as drafted.
                      </span>
                    )}
                    {t.saveError !== undefined && (
                      <span className="mo-chat-note">Could not save: {t.saveError}</span>
                    )}
                    {t.pptxError !== undefined && (
                      <span className="mo-chat-note">PowerPoint failed: {t.pptxError}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/*
          THE COMPOSER IS ONE BLOCK.
          
          It was three stacked rows: a row of native selects, then a bordered textarea, then a
          button beside it, each with its own edges. Four visible boxes for one action. It is now a
          single field that lights up as a whole on focus, with the controls that belong to the
          message sitting inside it on their own line, which is the shape the deck's own command bar
          already uses.
        */}
        <form className="mo-chat-composer" onSubmit={onSubmit}>
          <div className="mo-chat-box">
            <textarea
              ref={inputRef}
              className="mo-chat-input"
              value={text}
              rows={1}
              maxLength={2000}
              placeholder={
                lane === 'brain'
                  ? 'Ask the brain something\u2026'
                  : 'Who or what is this material for?'
              }
              onChange={(e) => setText(e.target.value)}
              /* Enter sends, Shift+Enter is a newline. The convention every chat uses. */
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />

            <div className="mo-chat-tools">
              {/* Only the drafting lane has anything to set. The brain lane's row stays empty
                  rather than growing controls it does not use. */}
              {lane === 'zou' && (
                <>
                  <label className="mo-pick">
                    <select
                      value={kind}
                      onChange={(e) => setKind(e.target.value as MaterialKind)}
                      aria-label="Material"
                    >
                      {KINDS.map((k) => (
                        <option key={k.value} value={k.value}>
                          {k.label}
                        </option>
                      ))}
                    </select>
                    <Icon name="chevron" size={11} />
                  </label>

                  {/*
                    The livery, as two visible buttons rather than a dropdown.

                    It decides the deck's plates AND the voice of the prose, and it cannot be
                    changed after drafting without paying for the draft again. A control that costs
                    a model call to correct should not look like a preference.
                  */}
                  <div className="mo-brand" role="group" aria-label="Livery">
                    {(['ntam', 'vantage'] as const).map((b) => (
                      <button
                        key={b}
                        type="button"
                        className="mo-brand-opt"
                        data-on={brand === b}
                        data-brand={b}
                        aria-pressed={brand === b}
                        onClick={() => {
                          setBrand(b);
                          setBrandPinned(true);
                        }}
                      >
                        {b === 'ntam' ? 'ntam' : 'vantage'}
                      </button>
                    ))}
                  </div>

                  {/* Why it is set that way, when something set it. Absent when he chose it. */}
                  {!brandPinned && inferred !== null && brand === inferred.brand && (
                    <span className="mo-brand-why">{inferred.why}</span>
                  )}
                </>
              )}

              <span className="mo-chat-spacer" />

              {/* Only near the ceiling. A counter on an empty box is noise; a silent truncation at
                  2,000 characters is worse, because a long brief would lose its end unannounced. */}
              {text.length > 1600 && (
                <span className="mo-chat-count" data-full={text.length >= 2000}>
                  {2000 - text.length} left
                </span>
              )}

              {/* Shift+Enter, said once and quietly, where the hands are. */}
              {text.length > 0 && !busy && <span className="mo-chat-hint">Shift + Enter for a line</span>}

              {busy ? (
                <button type="button" className="mo-chat-send" data-stop="true" onClick={stop}>
                  Stop
                </button>
              ) : (
                <button type="submit" className="mo-chat-send" disabled={text.trim() === ''}>
                  Send
                  <kbd>&crarr;</kbd>
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/*
 * Starters he can actually press.
 *
 * Chosen to be questions each lane answers WELL, not a sample of everything. Two of the brain's
 * are deliberately about gaps rather than facts, because that is what this vault is unusually
 * good at and what a search box never suggests.
 */
const BRAIN_STARTERS = [
  'What needs me today?',
  'Who have I neglected?',
  'What changed since yesterday?',
  'What do I owe on the Vantage rollout?',
] as const;

const ZOU_STARTERS = [
  'A follow-up email to the last meeting',
  'A pre-meeting briefing for my next call',
  'Talking points for the Boehringer account',
] as const;

/**
 * Seconds since this turn started asking.
 *
 * A count rather than a spinner. The lanes take about 25 seconds and a minute respectively, and a
 * spinner cannot distinguish "thinking" from "hung": at 40 seconds he needs to know it is still
 * going, and at 90 he needs to know it probably is not.
 */
function Elapsed(): JSX.Element {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="mo-chat-elapsed">
      {secs}s{secs >= 90 ? ' — longer than usual' : ''}
    </span>
  );
}

/** The button on the deck that opens it. */
export function ChatLauncher(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<AskRequest | null>(null);

  // Another surface asked to open it pre-filled. See `openAsk`.
  useEffect(() => {
    const onAsk = (e: Event): void => {
      /* A fresh object per event, so the overlay's per-request guard sees a new identity and
         answers a second Prepare instead of ignoring it. */
      setPending({ ...(e as CustomEvent<AskRequest>).detail });
      setOpen(true);
    };
    globalThis.addEventListener(ASK_EVENT, onAsk);
    return () => globalThis.removeEventListener(ASK_EVENT, onAsk);
  }, []);

  /*
   * `/` opens it, the convention every search-first app uses. Ignored while he is typing into a
   * field, or the key would be swallowed the moment he tried to type a slash into a note.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (el as HTMLElement | null)?.isContentEditable === true) return;
      e.preventDefault();
      setOpen(true);
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/*
        HE COULD NOT FIND IT. First reaction to the overlay was "I dont see the Chat?", with the
        launcher already on screen: a quiet outline button in a header that also holds the greeting,
        five annunciator chips and a search field. So it now reads as the primary action on the
        board, says what it opens rather than just "Ask", and carries the keyboard shortcut.
      */}
      <button
        type="button"
        className="mo-chat-launch"
        onClick={() => setOpen(true)}
        title="Ask the Brain or Ask Zou4Mo"
      >
        <Icon name="judgement" size={18} />
        <span>Ask the Brain or Zou4Mo</span>
        <kbd>/</kbd>
      </button>
      {open && <ChatOverlay onClose={() => { setOpen(false); setPending(null); }} initial={pending} />}
    </>
  );
}
